<template>
  <div>
    <Loader v-if="isLoading"></Loader>
    <div class="p-booking__filters">
      <div class="p-booking__title">{{ dictionary.availableView }}</div>
      <ScreeningsFilters
        v-if="desktop"
        v-model="selectedScreeningsFilters"
        :room-types-options="screeningsFiltersOptions.roomTypes"
        :screen-types-options="screeningsFiltersOptions.screenTypes"
        @filter-changed="onChangeFilter"
        @change="firstAvailableDay" />
    </div>

    <div class="p-booking__dates">
      <CalendarSlider
        v-model="selectedDate"
        :local="getLocale()"
        :breakpoints="calenderBreakPoints"
        :date-formate-options="{
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        }"
        :days-per-view="1"
        :allowed-dates="allowedDates"
        @choose-date="chooseDate"
        @days-data="initializeDays" />

      <Screenings
        v-if="showScreensOptions"
        id="screenStep"
        :screenings="filteredScreens"
        :selected-screen="store.bookingProgress.selectedRoom"
        @choose-screen="chooseScreen">
        <template #screen-features="{ screen }">
          <ScreenTypes
            :sphera="false"
            :dolby="screen.isDolby"
            :three-dimensions="screen.is3D"
            :imax="screen.isImax"
            :imax3d="screen.isImax3D"
            container-class="e-rooms--no-background" />
        </template>
        <template #room-type="{ screen }">
          <RoomTypeNames
            :screen="screen"
            :room-filters="store.allFilters.roomTypes" />
        </template>
      </Screenings>
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  getCurrentInstance,
  onBeforeMount,
  onMounted,
  ref,
  toRaw,
  watch,
} from 'vue'
import ScreeningsFilters from '../../ScreeningsFilters.vue'
import CalendarSlider from '../../CalendarSlider.vue'
import Screenings from '../../Screenings.vue'
import { useBookingStore } from '../../../store/BookingStore'
import useViewport from '../../../../features/viewport'
import { getData, getLocale } from '../../../../lib/lib'
import Loader from '../../../components/Shared/Loader.vue'
import ScreenTypes from '../../../components/Shared/ScreenTypes.vue'
import RoomTypeNames from '../../../components/RoomTypeNames.vue'
import { filterAllowedDates, filterScreens } from '../../../../lib/filtering'
import { sortBy, uniq, without } from 'underscore'
import postRequestHandler from '../../../../lib/postRequestHandler'
import constants from './../../../../lib/constants'

const { configuration, dictionary } =
  getCurrentInstance().appContext.config.globalProperties

const { desktop } = useViewport()
const store = useBookingStore()

const { filters, screens } = getData('bookingData')

const calendarDates = ref()
const allFilters = store.allFilters

const screeningsFiltersOptions = ref({
  screenTypes: [],
  roomTypes: [],
})

const filterLastClicked = ref(null)
const filteredScreens = ref([])
const isLoading = ref(false)

const calenderBreakPoints = {
  244: {
    slidesPerView: 2,
    spaceBetween: 8,
  },
  320: {
    slidesPerView: 2.5,
    spaceBetween: 8,
  },
  420: {
    slidesPerView: 3,
    spaceBetween: 8,
  },
  645: {
    slidesPerView: 3.5,
    spaceBetween: 8,
  },
  768: {
    slidesPerView: 6.5,
    spaceBetween: 8,
  },
  1280: {
    slidesPerView: 7,
    spaceBetween: 8,
  },
}

const selectedScreeningsFilters = ref({
  screenTypes: 'All',
  roomTypes: 'All',
})

const screeningsFiltersNames = [
  constants.filters.roomTypes,
  constants.filters.screenTypes,
]

// hooks
onMounted(() => {
  setPreselectedFilters()
  setScreeningsFilters()
  // await setFilteredCinemaScreens()
})

watch(
  () => store.bookingProgress.selectedCinema,
  () => {
    setScreeningsFilters()
    resetSelectedScreeningsFilters()
    firstAvailableDay()
  }
)

const setPreselectedFilters = () => {
  if (Object.keys(store.getSelectedFilters).length === 0) {
    return
  }
  if (store.getSelectedFilters.screenTypes !== 'All') {
    selectedScreeningsFilters.value.screenTypes =
      store.getSelectedFilters.screenTypes
  }

  if (store.getSelectedFilters.roomTypes !== 'All') {
    selectedScreeningsFilters.value.roomTypes =
      store.getSelectedFilters.roomTypes
  }
}

const selectedDate = computed({
  get() {
    return store.bookingProgress.selectedDay
      ? store.bookingProgress.selectedDay.dateValue
      : ''
  },
  set(val) {
    store.bookingProgress.selectedDay.dateValue = val
  },
})

onBeforeMount(() => {
  screeningsFiltersOptions.value = {
    screenTypes: toRaw(allFilters.screenTypes),
    roomTypes: toRaw(allFilters.roomTypes),
  }
})

const initializeDays = (dates) => {
  const firstAllowedDay = allowedDates.value[0]
  calendarDates.value = JSON.parse(JSON.stringify(dates.value))

  const entry = calendarDates.value.find((x) => x.dateValue === firstAllowedDay)

  const { selectedDay } = store.bookingProgress

  // set firstAllowedDay only if there is no stored date at state
  // this happens when the date is preselected when user comes from a movie page booking component
  if (entry && !selectedDay) {
    chooseDate(entry)
  }
}

const firstAvailableDay = () => {
  const firstAllowedDay = allowedDates.value[0]
  const entry = calendarDates.value.find((x) => x.dateValue === firstAllowedDay)
  if (entry) {
    chooseDate(entry)
  }
}

const showScreensOptions = computed(() => {
  return (
    store.bookingProgress.selectedDay &&
    Object.keys(store.bookingProgress.selectedDay).length
  )
})

const allowedDates = computed(() => {
  const { selectedMovie, selectedCinema } = store.bookingProgress
  return filterAllowedDates(
    selectedScreeningsFilters.value.roomTypes,
    selectedScreeningsFilters.value.screenTypes,
    selectedCinema.value,
    selectedMovie,
    allFilters.screens,
    configuration.movieTimeOffset
  )
})

const chooseDate = async (date) => {
  store.selectDate(date)
  await setFilteredCinemaScreens()
}

const resetSelectedScreeningsFilters = () => {
  selectedScreeningsFilters.value.roomTypes = 'All'
  selectedScreeningsFilters.value.screenTypes = 'All'
}

const chooseScreen = async (screen) => {
  store.selectRoom(screen)
  await setFilteredCinemaScreens()
}

const onChangeFilter = async (value, name) => {
  filterLastClicked.value = name

  selectedScreeningsFilters.value[name] = value

  store.selectDate(null)
  store.selectRoom(null)
  setScreeningsFilters()
  // await setFilteredCinemaScreens()
  firstAvailableDay()
}

const setScreeningsFilters = () => {
  screeningsFiltersNames.forEach((element) => {
    if (element === filterLastClicked.value) {
      return
    }

    let scrns = screens.filter(
      (i) =>
        i.scheduledFilmId === store.bookingProgress.selectedMovie.id &&
        i.cinemaId === store.bookingProgress.selectedCinema.value
    )

    const others = without(screeningsFiltersNames, element)

    others.forEach((x) => {
      const value = selectedScreeningsFilters.value[x]

      if (value === 'All') {
        return
      }

      switch (x) {
        case constants.filters.roomTypes:
          scrns = scrns.filter((i) => i[x].includes(value))
          break
        case constants.filters.screenTypes:
          scrns = scrns.filter((i) => i.screenType === value)
          break
        default:
          console.log(`Sorry, we are out of ${element}.`)
      }
    })

    let entries = []

    switch (element) {
      case constants.filters.roomTypes:
        entries = uniq(scrns.map((i) => i[element]).flat())
        break
      case constants.filters.screenTypes:
        entries = uniq(scrns.map((i) => i.screenType))
        break
      default:
        console.log(`Sorry, we are out of ${element}.`)
    }

    screeningsFiltersOptions.value[element] = entries.map((x) => ({
      value: x,
      display: filters[element].find((y) => y.value === x)?.display,
    }))
  })
}

const setFilteredCinemaScreens = async () => {
  const { roomTypes, screenTypes } = selectedScreeningsFilters.value
  const screens = sortBy(store.getAllCinemaScreens, 'showtime')
  const entries = filterScreens(
    roomTypes,
    screenTypes,
    screens,
    configuration.movieTimeOffset
  )

  const { selectedMovie, selectedCinema, selectedDay } = store.bookingProgress

  const postData = {
    filmId: selectedMovie.id,
    cinemaId: selectedCinema.value,
    date: selectedDay.dateValue,
  }

  isLoading.value = true
  await postRequestHandler({
    postData,
    endpoint: configuration.endpointScreenAvailability,
    successCallback: (response) => {
      if (response.hasError) {
        entries.forEach((entry) => {
          entry.soldoutStatus = 1
          entry.isLimited = true
        })
      } else {
        entries.forEach((entry) => {
          const screen = response.data.availability.find(
            (x) => x.screenId === entry.id
          )
          entry.soldoutStatus = screen?.soldoutStatus
          entry.isLimited = screen?.isLimited
        })
      }
      filteredScreens.value = entries
      isLoading.value = false
    },
    errorCallback: () => {
      entries.forEach((entry) => {
        entry.soldoutStatus = 1
        entry.isLimited = true
      })
      filteredScreens.value = entries
      isLoading.value = false
    },
    refreshTokenHandler: configuration.refresh,
  })
}
</script>

<style lang="scss" scoped>
.loader {
  position: fixed;
}
</style>
